// What a SICKW check says a phone is: its model, storage, color, carrier and whether it's locked,
// read from the check's lines and matched to the store's model list when there is one. Buy phone
// and Buy these fill in the phone with it; Phone check prints the price sticker from it.
import type { ImeiCheckSection } from './buyback.js';
import { CARRIERS, isValidImei } from './format.js';

/** The first line (in the answers that worked) whose label matches. */
export function findLine(sections: ImeiCheckSection[], re: RegExp): [string, string] | null {
  for (const s of sections) {
    if (!s.ok) continue;
    for (const f of s.fields) if (f[0] && re.test(f[0])) return f;
  }
  return null;
}

export interface Suggestion { model?: string; storage?: string; color?: string; carrier?: string; locked?: boolean; text: string }

const norm = (s: string) => ` ${s.toLowerCase().replace(/\[[^\]]*\]|\([^)]*\)/g, ' ').replace(/\+/g, ' plus ').replace(/promax/g, 'pro max').replace(/[^a-z0-9]+/g, ' ').trim()} `;

/** The lines that name the phone: "Model: iPhone 12 Pro 128GB Graphite [A2341]", "Model Desc: Galaxy S22+ 5G". */
const NAME_LABELS = [/^model$/i, /^model desc/i, /^desc(ription)?$/i, /^model name$/i];
const nameLines = (sections: ImeiCheckSection[]) => NAME_LABELS.map((re) => findLine(sections, re)?.[1]).filter(Boolean) as string[];

const WORDS: Record<string, string> = { iphone: 'iPhone', ipad: 'iPad', ipod: 'iPod', imac: 'iMac', macbook: 'MacBook', airpods: 'AirPods', se: 'SE', xr: 'XR', xs: 'XS', '5g': '5G', fe: 'FE', usa: 'USA' };

/** Words SICKW writes in capitals ("IPHONE 13 PRO", "SIERRA BLUE") as a name ("iPhone 13 Pro", "Sierra Blue"). */
export function tidyWords(s: string): string {
  const t = s.replace(/\s+/g, ' ').trim();
  if (/[a-z]/.test(t)) return t;
  return t.toLowerCase().split(' ').map((w) => WORDS[w] ?? w.replace(/^[a-z]/, (c) => c.toUpperCase())).join(' ');
}

/** Apple's short color words on SICKW's model descriptions ("SLV", "NTRL TTNM", "GLA"), spelled out. */
const COLOR_WORDS: Record<string, string> = {
  blk: 'Black', blck: 'Black', wht: 'White', slv: 'Silver', slvr: 'Silver', gld: 'Gold', gry: 'Gray', grey: 'Gray', gray: 'Gray',
  blu: 'Blue', grn: 'Green', pnk: 'Pink', ylw: 'Yellow', yllw: 'Yellow', prp: 'Purple', prpl: 'Purple', org: 'Orange', orng: 'Orange',
  crl: 'Coral', lav: 'Lavender', lvnd: 'Lavender', lvndr: 'Lavender', ultrmrn: 'Ultramarine', mdnt: 'Midnight', mdnght: 'Midnight',
  strlt: 'Starlight', strlght: 'Starlight', grph: 'Graphite', grpht: 'Graphite', ttnm: 'Titanium', ttn: 'Titanium', ntrl: 'Natural',
  dsrt: 'Desert', spc: 'Space', srra: 'Sierra', alpn: 'Alpine', pcfc: 'Pacific', rs: 'Rose', jt: 'Jet', slt: 'Slate', dp: 'Deep',
  drk: 'Dark', dk: 'Dark', lt: 'Light', lght: 'Light', cld: 'Cloud', gla: 'Glacier', glcr: 'Glacier', brg: 'Burgundy', burg: 'Burgundy',
  brgndy: 'Burgundy', brgdy: 'Burgundy', csmc: 'Cosmic', prdct: 'Product', prod: 'Product',
};

/** Apple's full names where SICKW's code only says the shade: an iPhone 17 Pro "BLU" is Deep Blue. */
const MODEL_COLORS: [RegExp, Record<string, string>][] = [
  [/iphone 17 pro/i, { Blue: 'Deep Blue', Orange: 'Cosmic Orange' }],
];

/** Not colors: the market codes beside the size ("NAUS", "USA"). */
const REGION = /^(usa?|ww|na(us|mm|ca)?|eu|uk|jp|cn|hk|kr|au|ca|mx|la|lla?|za|ch|zp|in|intl)$/i;

/** "SLV" → "Silver", "NTRL TTNM" → "Natural Titanium", "BLUE" → "Blue". A code it doesn't know stays as SICKW wrote it. */
export function colorName(raw: string, model = ''): string {
  const words = raw.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  if (!words.length) return '';
  const name = words.map((w) => COLOR_WORDS[w.toLowerCase()] ?? (/^[A-Z]{2,4}$/.test(w) && !/[AEIOUY]/.test(w) ? w : tidyWords(w))).join(' ').replace(/\b[a-z]/g, (c) => c.toUpperCase());
  for (const [re, names] of MODEL_COLORS) if (re.test(model) && names[name]) return names[name];
  return name;
}

/**
 * SICKW's newer Apple descriptions are comma lists: "IPHONE 17 PRO MAX,NAUS,512GB,SLV",
 * "IPHONE 16 PROMAX,NAMM,256GB,NTRL TTNM" (the name, a market code, the size, the color).
 */
export function commaParts(raw: string): { name: string; size: string; color: string } | null {
  const parts = raw.replace(/\[[^\]]*\]|\([^)]*\)/g, ' ').split(',').map((p) => p.trim()).filter(Boolean);
  const at = parts.findIndex((p) => /^\d+\s*(GB|TB)$/i.test(p));
  if (parts.length < 3 || at < 1) return null;
  const after = parts.slice(at + 1).filter((p) => !REGION.test(p)).join(' ');
  const before = parts.slice(1, at).filter((p) => !REGION.test(p)).join(' ');
  return { name: parts[0], size: parts[at], color: after || before };
}

/** What SICKW says the phone is, matched to the website's models, storage and carriers. */
export function suggestFromCheck(sections: ImeiCheckSection[], models: string[], storagesOf: (m: string) => string[], carriers: readonly string[]): Suggestion | null {
  if (!sections.some((s) => s.ok)) return null;
  const s: Suggestion = { text: '' };
  const names = nameLines(sections);
  for (const raw of names) {
    const n = norm(raw);
    const hit = models.filter((m) => n.includes(norm(m))).sort((a, b) => b.length - a.length)[0];
    if (hit) { s.model = hit; break; }
  }
  const full = names.join(' ');
  const size = /(\d+)\s*(GB|TB)\b/i.exec(full);
  if (size && s.model) s.storage = storagesOf(s.model).find((x) => x.replace(/\s/g, '').toUpperCase() === `${size[1]}${size[2].toUpperCase()}`);
  // The color: from a comma list ("IPHONE 18 PRO,NAUS,256GB,GLA"), else the words after the size
  // ("iPhone 11 64GB Black Cellular [A2111]", without the radio).
  const comma = names.map(commaParts).find((p) => p?.color);
  if (comma) s.color = colorName(comma.color, s.model ?? comma.name);
  const colorM = /\b\d+\s*(?:GB|TB)\s+([A-Za-z][A-Za-z ]{1,28}?)\s*(?:\[|\(|$)/i.exec((names[0] ?? '').replace(/-(usa|us|ww)\b/i, ' '));
  const colorWords = colorM ? colorM[1].replace(/\b(cellular|wi-?fi|lte|5g|gsm|cdma|unlocked)\b.*$/i, '').trim() : '';
  if (!s.color && colorWords && !REGION.test(colorWords)) s.color = colorName(colorWords, s.model ?? names[0]);
  // Some checks (Samsung info, model & color) have a line of its own for the color.
  const colorLine = findLine(sections, /^colou?r$/i)?.[1]?.trim() ?? '';
  if (!s.color && colorLine && colorLine.length <= 30 && /[a-z]/i.test(colorLine)) s.color = tidyWords(colorLine);
  const simLock = findLine(sections, /sim.?lock/i)?.[1] ?? '';
  const carrierLine = findLine(sections, /^(locked carrier|carrier)$/i)?.[1] ?? '';
  const named = carriers.filter((c) => !/unlocked|other/i.test(c)).sort((a, b) => b.length - a.length).find((c) => carrierLine.toLowerCase().includes(c.toLowerCase()) || (/metro/i.test(c) && /metro/i.test(carrierLine)));
  if (/unlock/i.test(simLock) || (!simLock && /^unlocked/i.test(carrierLine))) { s.carrier = 'Unlocked'; s.locked = false; }
  // No SIM-lock line: "AT&T US - Locked" on the carrier line says it.
  else if (named) { s.carrier = named; if (/lock/i.test(simLock) || (!simLock && /\blocked\b/i.test(carrierLine))) s.locked = true; }
  s.text = [s.model, s.storage, s.color, s.carrier ? `${s.carrier}${s.locked ? ' (locked)' : ''}` : null].filter(Boolean).join(' · ');
  return s.text ? s : null;
}

// ---------------------------------------------------------------- the price sticker from a check

/** A saved Phone check, as the screens get it. */
export interface CheckLike {
  imei: string;
  service: { id: string; name: string };
  status: string;
  lines?: { label: string; value: string }[];
}

/** The store's phones: the models (with their storage sizes) of each brand, and the carriers. */
export interface ModelList {
  brands: Record<string, Record<string, string[]>>;
  carriers?: readonly string[] | null;
}

/** The phone as a check describes it. Empty when SICKW didn't say. */
export interface CheckedPhone {
  brand: string;
  model: string;
  storage: string;
  color: string;
  /** A carrier on the list ("T-Mobile"), "Unlocked", or "" when SICKW didn't say. */
  carrier: string;
  /** Null when SICKW didn't say whether it's locked. */
  lock: 'locked' | 'unlocked' | null;
  imei: string | null;
  serial: string | null;
}

const BRAND_HINTS: [RegExp, string][] = [[/iphone|ipad|apple|macbook|airpods/i, 'Apple'], [/galaxy|samsung|\bsm-[a-z]/i, 'Samsung'], [/pixel|google/i, 'Google'], [/motorola|\bmoto\b/i, 'Motorola']];

/** The brand a check's words point to, or null. */
export function brandHint(text: string): string | null {
  return BRAND_HINTS.find(([re]) => re.test(text))?.[1] ?? null;
}

/** "IPHONE 13 PRO MAX 256GB SIERRA BLUE [A2484]" → "iPhone 13 Pro Max": the size, the color and the codes come off. */
export function tidyModelName(raw: string, brand = ''): string {
  let s = (commaParts(raw)?.name ?? raw).replace(/\[[^\]]*\]|\([^)]*\)/g, ' ').replace(/\b\d+\s*(GB|TB)\b.*$/i, ' ').replace(/\bPROMAX\b/i, 'PRO MAX');
  s = tidyWords(s);
  if (brand && s.toLowerCase().startsWith(`${brand.toLowerCase()} `)) s = s.slice(brand.length + 1);
  return s.slice(0, 60).trim();
}

/** The line that reads like a name ("Galaxy S22+ 5G"), not a model number ("SM-S906U"). */
function phoneName(sections: ImeiCheckSection[]): string {
  const names = nameLines(sections);
  return names.find((n) => /\s/.test(n.replace(/\[[^\]]*\]|\([^)]*\)/g, ' ').trim())) ?? names[0] ?? '';
}

/** "256GB", from the name or a capacity line. */
function sizeOf(sections: ImeiCheckSection[]): string {
  const m = /(\d+)\s*(GB|TB)\b/i.exec(nameLines(sections).join(' ')) ?? /(\d+)\s*(GB|TB)\b/i.exec(findLine(sections, /^(capacity|storage)$/i)?.[1] ?? '');
  return m ? `${Number(m[1])}${m[2].toUpperCase()}` : '';
}

/**
 * The phone a check describes, for its price sticker: matched to the store's model list when it's
 * there (the same names as inventory), or tidied from SICKW's own words. The IMEI is the one
 * checked, or the one in the result when the check ran on a serial number.
 */
export function phoneFromCheck(check: CheckLike, list: ModelList | null): CheckedPhone {
  const id = check.imei.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const lines = check.lines ?? [];
  const sections: ImeiCheckSection[] = [{ service: check.service.id, title: check.service.name, ok: check.status === 'ok', fields: lines.map((l): [string, string] => [l.label, l.value]) }];
  const byImei = /^\d{15}$/.test(id) && isValidImei(id);
  let imei = byImei ? id : null;
  if (!imei) {
    const v = (findLine(sections, /^imei ?(1|number)?$/i)?.[1] ?? '').replace(/\D/g, '');
    if (isValidImei(v)) imei = v;
  }
  const out: CheckedPhone = { brand: '', model: '', storage: '', color: '', carrier: '', lock: null, imei, serial: byImei ? null : id || null };
  if (check.status !== 'ok') return out;
  const hinted = brandHint(`${check.service.name} ${lines.map((l) => `${l.label} ${l.value}`).join(' ')}`);
  const carriers = list?.carriers?.length ? list.carriers : CARRIERS;
  const brands = Object.keys(list?.brands ?? {});
  let s: Suggestion | null = null;
  for (const b of hinted ? [hinted, ...brands.filter((x) => x !== hinted)] : brands) {
    const models = Object.keys(list?.brands[b] ?? {});
    if (!models.length) continue;
    const hit = suggestFromCheck(sections, models, (m) => list?.brands[b]?.[m] ?? [], carriers);
    if (hit?.model) { s = hit; out.brand = b; break; }
  }
  s ??= suggestFromCheck(sections, [], () => [], carriers);
  out.brand ||= hinted ?? '';
  out.model = s?.model ?? tidyModelName(phoneName(sections), out.brand);
  out.storage = s?.storage ?? sizeOf(sections);
  out.color = s?.color ?? '';
  if (s?.carrier) {
    out.carrier = s.carrier;
    out.lock = s.carrier === 'Unlocked' || s.locked === false ? 'unlocked' : s.locked ? 'locked' : null;
  } else if (/\block/i.test(findLine(sections, /sim.?lock/i)?.[1] ?? '')) {
    // Locked, to a carrier that isn't on the list.
    out.lock = 'locked';
  }
  return out;
}

// ---------------------------------------------------------------- Buy phone: filled in from its check

/** A Buy phone check (one or more SICKW answers) read as one check, for phoneFromCheck. */
export function checkFromSections(code: string, sections: ImeiCheckSection[]): CheckLike {
  const ok = sections.filter((s) => s.ok);
  const first = ok[0] ?? sections[0];
  return {
    imei: code,
    service: { id: first?.service ?? '', name: first?.title ?? '' },
    status: ok.length ? 'ok' : 'error',
    lines: ok.flatMap((s) => s.fields.map(([label, value]) => ({ label, value }))),
  };
}

/** What's on the Buy phone form when its check comes back. */
export interface BuyFormPhone {
  brand: string;
  model: string;
  storage: string;
  color: string;
  carrier: string;
  /** Staff picked the carrier or the lock themselves: the check leaves it alone. */
  carrierPicked: boolean;
}

export interface Filled {
  model?: string;
  storage?: string;
  color?: string;
  carrier?: string;
  locked?: boolean;
  /** What was filled in, in words: "iPhone 12 Pro", "128GB", "Graphite", "T-Mobile, locked". */
  what: string[];
  /** The check says it's another brand's phone (one of the brands on the list): nothing is filled in. */
  otherBrand?: string;
}

/**
 * What a check fills in on Buy phone: only the boxes still empty (and the carrier until staff pick
 * one), never what staff typed. The storage only when it's one of the model's sizes (any size
 * when the website has none for it). `brands`: the brands staff can pick.
 */
export function fillBlanks(form: BuyFormPhone, found: CheckedPhone, storagesOf: (model: string) => string[], brands: readonly string[]): Filled {
  if (found.brand && found.brand !== form.brand && brands.includes(found.brand)) return { what: [], otherBrand: found.brand };
  const out: Filled = { what: [] };
  const typed = form.model.trim();
  if (!typed && found.model) {
    out.model = found.model;
    out.what.push(found.model);
  }
  const model = typed || found.model;
  if (!form.storage && found.storage && model) {
    const sizes = storagesOf(model);
    const same = (s: string) => s.replace(/\s/g, '').toUpperCase() === found.storage.replace(/\s/g, '').toUpperCase();
    const size = sizes.length ? sizes.find(same) : found.storage;
    if (size) {
      out.storage = size;
      out.what.push(size);
    }
  }
  if (!form.color.trim() && found.color) {
    out.color = found.color;
    out.what.push(found.color);
  }
  if (!form.carrierPicked && found.carrier) {
    out.carrier = found.carrier;
    out.locked = found.carrier !== 'Unlocked' && found.lock === 'locked';
    out.what.push(found.carrier === 'Unlocked' ? 'Unlocked' : `${found.carrier}${out.locked ? ', locked' : ''}`);
  }
  return out;
}
